import { useCallback, useEffect, useRef, useState } from "react";
import { isAuthoritativeScoreSheet } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";

// #2070 — batch-fetches every ScoreSheet across the running order's rides in
// ONE call (score-api's POST /scoresheet/batch, card #1961), grouped by
// rideId, so orderOfGo.js's deriveRideTermReason() call can resolve tier 4
// (a ScoreSheet's own eliminations[]) for a ride whose elimination hasn't
// been denormalized onto ride.terminationReason — the un-migrated #2056
// legacy shape. Mirrors scoreadmin-fe's ClassPlacings.jsx loadResults/
// applyResults grouping (card #2063 AC3), same client key (`scoreSheets`).
//
// Deliberately NOT polled on the same cadence as the status poll: a LIVE
// elimination submitted today is already stamped onto ride.terminationReason
// by score-api's scoresheet handler in the SAME request that saves it (tier
// 1 resolves it immediately, no sheets fetch needed) — this call only exists
// to catch the legacy gap, so a fetch whenever the running order's ride-id
// set changes, plus the same manual "refresh now" affordance every other
// data source here already has, is enough. Best-effort: a failed fetch
// degrades to no sheets (tiers 1-3 still resolve everything that isn't
// relying on tier 4) rather than blocking the running order.
// #2972 - a same-seat contender (#2815) must never feed
// deriveRideTermReason's tier-4 elimination lookup: no reader treats a
// contender as the ride's real sheet. Exported (same reason
// useRidesByClassId's groupSheetsByRide is, scribe-fe) so the filter is
// directly testable without rendering the hook.
export function groupAuthoritativeSheetsByRideId(sheets) {
  const next = new Map();
  for (const sheet of sheets ?? []) {
    if (!sheet?.rideId || !isAuthoritativeScoreSheet(sheet)) continue;
    const list = next.get(sheet.rideId);
    if (list) list.push(sheet);
    else next.set(sheet.rideId, [sheet]);
  }
  return next;
}

export function useRideSheets(rideIds, { enabled = true } = {}) {
  const scoreApi = useScoreApi();
  const [sheetsByRideId, setSheetsByRideId] = useState(new Map());

  const ids = Array.from(new Set((rideIds ?? []).filter(Boolean)));
  const idsKey = ids.join(",");
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const refresh = useCallback(async () => {
    const currentIds = idsRef.current;
    if (!currentIds.length) {
      setSheetsByRideId(new Map());
      return;
    }
    try {
      const sheets = await scoreApi.scoreSheets
        .listScoreSheetsForRides({ rideIds: currentIds })
        .then(unwrap);
      setSheetsByRideId(groupAuthoritativeSheetsByRideId(sheets));
    } catch {
      // Degrade silently — see the doc comment above.
    }
  }, [scoreApi]);

  useEffect(() => {
    if (!enabled || !idsRef.current.length) return;
    refresh();
    // idsKey drives re-fetch when the running order's ride-id set changes;
    // refresh is stable across renders (only scoreApi in its deps).
  }, [idsKey, enabled, refresh]);

  return { sheetsByRideId, refresh };
}

export default useRideSheets;
